import type { IAlertCollapseConfig, IAlertCollapseBuild } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';

import * as exElement from '../../utils/element';
import * as exIcon from '../../utils/icon';
import * as exButton from '../../components/button';

// ----------------------------------------------------------------------------

function create(config: IAlertCollapseConfig = {}): IAlertCollapseBuild {
    const defConfig: IAlertCollapseConfig = {
        expanded: false,
        toggle: {
            chevron: true,
        },
    };
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const build = {} as IAlertCollapseBuild;

    // Create element (wrapper)
    const elWrapper = exElement.create({
        merge: defConfig,
        class: {
            new: 'alert alert-collapse',
        },
    });
    build.element = elWrapper;

    // If using type
    if (typeof defConfig.type === 'string') {
        addClass(elWrapper, 'alert-' + defConfig.type);
    }

    // ------------------------------------------------------------------------

    // Create element (toggle button)
    const toggleButton = exButton.create({
        merge: defConfig.toggle,
        class: {
            new: 'alert-collapse-toggle',
        },
        attr: {
            'aria-expanded': defConfig.expanded === true ? 'true' : 'false',
        },
        bootstrap: { enabled: false },
    });
    build.toggle = toggleButton.element;
    elWrapper.appendChild(toggleButton.element);

    // ------------------------------------------------------------------------

    // Settings (toggle content)
    settings(build, defConfig);

    // ------------------------------------------------------------------------

    // Create element (body)
    const elBody = exElement.create({
        merge: defConfig.body,
        class: {
            new: 'alert-collapse-body',
        },
    });
    build.body = elBody;

    if (defConfig.expanded !== true) {
        elBody.hidden = true;
    }

    if (defConfig.body?.scroll === true) {
        addClass(elBody, 'alert-collapse-body-scroll');
    }

    elWrapper.appendChild(elBody);

    // ------------------------------------------------------------------------

    // Toggle event
    toggleButton.element.addEventListener('click', function (): void {
        const isExpanded = toggleButton.element.getAttribute('aria-expanded') === 'true';
        toggleButton.element.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        elBody.hidden = isExpanded;
    });

    // ------------------------------------------------------------------------

    return build;
}

function settings(build: IAlertCollapseBuild, config: IAlertCollapseConfig = {}): void {
    const elToggle = build.toggle;

    if (isEmptyObject(config.toggle)) {
        return;
    }

    // If using label
    if (config.toggle?.label != null) {
        const elLabel = exElement.create({
            merge: typeof config.toggle.label === 'string' ? { html: config.toggle.label } : config.toggle.label,
            class: {
                new: 'alert-collapse-label',
            },
        });
        elToggle.appendChild(elLabel);
    }

    // If using chevron
    if (config.toggle?.chevron != null && config.toggle.chevron !== false) {
        const chevronConfig = typeof config.toggle.chevron === 'object' ? config.toggle.chevron : {};
        const elChevron = exIcon.create({
            merge: chevronConfig,
            class: {
                new: 'alert-collapse-chevron',
            },
            type: 'fa-solid fa-chevron-down',
        });
        elToggle.appendChild(elChevron);
    }
}

// ----------------------------------------------------------------------------

export { create };
